import { formatEur, type MemberDto } from "@gladis/shared";
import { ActionMenu, EmptyState, PersonLabel, Table } from "@gladis/ui";
import { incomeCategoryLabel, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomeEntryDto } from "../dto.js";

/**
 * One-off receipts, newest first.
 *
 * The columns a one-off has and a source does not: a single DATE rather than a range, and the source
 * it belongs to. Vakantiegeld belongs to a salary; a tax refund belongs to nothing, and the column
 * says which without the reader having to open anything.
 *
 * A FUTURE-DATED ENTRY IS MARKED RATHER THAN REFUSED. Somebody who knows December's payment now
 * should be able to record it; it contributes nothing until that month arrives, and saying so on the
 * row is more use than a validation error.
 */
export interface EntryTableProps {
  entries: readonly IncomeEntryDto[];
  /** The household's members, for their faces (020). */
  members?: readonly MemberDto[];
  /** For naming which source an entry belongs to. */
  sourceNames: Map<string, string>;
  onEdit: (entry: IncomeEntryDto) => void;
  onDelete: (entry: IncomeEntryDto) => void;
}

export function EntryTable({
  entries,
  sourceNames,
  members = [],
  onEdit,
  onDelete,
}: EntryTableProps): ReactNode {
  const t = useT();
  if (entries.length === 0) {
    return <EmptyState>{t("inc.entry.empty")}</EmptyState>;
  }

  return (
    <Table aria-label={t("inc.entry.heading")}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("inc.entry.colPayment")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.colWho")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.source.category")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.entry.partOf")}</Table.HeaderCell>
          <Table.HeaderCell>{t("inc.entry.colArrived")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("inc.source.amount")}</Table.HeaderCell>
          <Table.HeaderCell>
            <span className="sr-only">{t("inc.source.actions")}</span>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {entries.map((entry) => (
          <Table.Row key={entry.id}>
            <Table.Cell>
              <span className="font-medium">{entry.description}</span>
              {entry.futureDated ? (
                <span className="ml-2 text-xs text-muted">not yet arrived</span>
              ) : null}
            </Table.Cell>
            <Table.Cell>
              <PersonLabel
                label={
                  entry.ownership === "joint"
                    ? t("person.joint")
                    : (entry.ownerLabel ?? t("person.formerMember"))
                }
                isPerson={entry.ownership !== "joint" && entry.ownerLabel !== null}
                avatarUrl={members.find((m) => m.userId === entry.ownerId)?.avatarUrl}
                {...(entry.ownership === "joint"
                  ? {
                      group: members.map((m) => ({
                        name: m.displayName,
                        avatarUrl: m.avatarUrl,
                      })),
                    }
                  : {})}
              />
            </Table.Cell>
            <Table.Cell>{incomeCategoryLabel(entry.category)}</Table.Cell>
            <Table.Cell>
              {entry.incomeSourceId === null ? "—" : (sourceNames.get(entry.incomeSourceId) ?? "—")}
            </Table.Cell>
            <Table.Cell>{entry.receivedOn}</Table.Cell>
            <Table.Cell numeric>{formatEur(entry.amountInEuros)}</Table.Cell>
            <Table.Cell>
              <ActionMenu
                label={`Actions for ${entry.description}`}
                items={[
                  { label: t("common.edit"), onSelect: () => onEdit(entry) },
                  { label: t("common.delete"), onSelect: () => onDelete(entry), destructive: true },
                ]}
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
